import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  alpha,
  Box,
  Collapse,
  IconButton,
  List,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivationChecklistItem } from "../ActivationChecklistItem";
import { ActivationProgress } from "../ActivationProgress";
import { CompletionView } from "../CompletionView";
import { BLURPLE, EASING, fadeIn, TIMING } from "../animations";
import { ChecklistVariantProps } from "../types";

/**
 * Sidebar Checklist Variant
 *
 * Inspiration: Notion's "Getting Started" checklist, Intercom's floating cards
 * Location: Fixed position bottom-right, above other FABs
 *
 * Enhanced with:
 * - Gradient header with BLURPLE accent
 * - Smooth expand/collapse with icon rotation
 * - Current step title shown when collapsed
 * - Completion celebration view
 * - Premium shadow and border styling
 */
export function SidebarChecklist({
  activation,
  steps,
  currentStepIndex,
  completedCount,
  onStepClick,
}: ChecklistVariantProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const totalSteps = steps.length;
  const isComplete = completedCount >= totalSteps;
  const currentStep = steps[currentStepIndex];

  // Don't render if dismissed after completion
  if (dismissed) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: "fixed",
        bottom: 16,
        right: 74,
        width: 340,
        zIndex: 2600,
        bgcolor: "background.paper",
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.8),
        overflow: "hidden",
        // Premium shadow
        boxShadow: `
          0 1px 2px ${alpha(theme.palette.common.black, 0.04)},
          0 4px 12px ${alpha(theme.palette.common.black, 0.08)},
          0 8px 24px ${alpha(theme.palette.common.black, 0.06)}
        `,
        // Entrance animation
        animation: `${fadeIn} ${TIMING.normal} ${EASING.smooth}`,
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
        },
      }}
    >
      {/* Header - always visible */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          // Gradient background with BLURPLE accent
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(BLURPLE, 0.2)} 0%, ${alpha(
                  BLURPLE,
                  0.08
                )} 100%)`
              : `linear-gradient(135deg, ${alpha(BLURPLE, 0.12)} 0%, ${alpha(
                  BLURPLE,
                  0.04
                )} 100%)`,
          borderBottom: "1px solid",
          borderColor: alpha(BLURPLE, 0.15),
          transition: `background ${TIMING.fast} ${EASING.smooth}`,
          "&:hover": {
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(BLURPLE, 0.25)} 0%, ${alpha(
                    BLURPLE,
                    0.12
                  )} 100%)`
                : `linear-gradient(135deg, ${alpha(BLURPLE, 0.16)} 0%, ${alpha(
                    BLURPLE,
                    0.06
                  )} 100%)`,
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <RocketLaunchIcon
            fontSize="small"
            sx={{
              color: BLURPLE,
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ color: "text.primary" }}
            >
              {t("components.activation.title")}
            </Typography>
            {/* Show current step title when collapsed */}
            {!expanded && currentStep && !isComplete && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.7rem",
                  mt: -0.25,
                }}
              >
                {t(currentStep.titleKey)}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          {!expanded && (
            <ActivationProgress
              completed={completedCount}
              total={totalSteps}
              variant="dots"
              showLabel={false}
              size="small"
            />
          )}
          <IconButton
            size="small"
            sx={{
              color: BLURPLE,
              p: 0.5,
              transition: `transform ${TIMING.normal} ${EASING.smooth}`,
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Expanded content OR Completion view */}
      <Collapse in={expanded}>
        {isComplete ? (
          <CompletionView onDismiss={() => setDismissed(true)} />
        ) : (
          <>
            <Box
              sx={{
                px: 1.5,
                pt: 1,
                pb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("components.activation.subtitle")}
              </Typography>
              <ActivationProgress
                completed={completedCount}
                total={totalSteps}
                variant="dots"
                showLabel={false}
                size="small"
              />
            </Box>
            <List dense disablePadding sx={{ px: 0.5, pb: 0.5 }}>
              {steps.map((step, index) => {
                const isStepCompleted = !!activation[step.completedAtField];
                const isCurrent = index === currentStepIndex;
                return (
                  <ActivationChecklistItem
                    key={step.id}
                    step={step}
                    isCompleted={isStepCompleted}
                    isCurrent={isCurrent}
                    onClick={() => onStepClick(step)}
                    stepNumber={index + 1}
                    animationDelay={index * 50}
                  />
                );
              })}
            </List>
          </>
        )}
      </Collapse>
    </Paper>
  );
}
